import React, { useState, useEffect, useMemo } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
//import { Card } from 'primereact/card';
import { Message } from 'primereact/message';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import layoutService from '../services/layoutService';

const ResponsiveGridLayout = WidthProvider(Responsive);

const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };

const UnknownWidget = ({ type }) => (
  <Message
    severity="error"
    text={`No widget registered for type "${type}". Check that src/widgets/${type}/index.js exists.`}
    className="w-full"
  />
);

/**
 * Read-only dashboard grid — sourced from layoutService (localStorage),
 * with dragging/resizing disabled. Stays in sync automatically if the
 * editable version (or another tab) changes the layout.
 *
 * @param {Array} [fallbackLayout] - used only if nothing is saved yet
 *   under `storageKey`: { i, x, y, w, h, title, type, config }
 * @param {Object} widgetRegistry - map of `type` -> { Component, defaultConfig }
 * @param {string} [storageKey] - localStorage key to read from (must match
 *   the key used by DashboardGridEditable to see its changes)
 */
const DashboardGridReadOnly = ({
  fallbackLayout = [],
  widgetRegistry = {},
  storageKey = layoutService.DEFAULT_STORAGE_KEY,
}) => {
  const [items, setItems] = useState(() => layoutService.getLayout(storageKey, fallbackLayout));

  // Re-read once if storageKey changes, and stay live-synced afterwards.
  useEffect(() => {
    setItems(layoutService.getLayout(storageKey, fallbackLayout));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  useEffect(
    () => layoutService.subscribe(storageKey, setItems, fallbackLayout),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [storageKey]
  );

  const itemsById = useMemo(() => {
    const map = {};
    items.forEach((item) => {
      map[item.i] = item;
    });
    return map;
  }, [items]);

  const rglLayout = useMemo(
    () => items.map(({ i, x, y, w, h }) => ({ i, x, y, w, h })),
    [items]
  );

  return (
    <ResponsiveGridLayout
      className="layout"
      layouts={{ lg: rglLayout }}
      breakpoints={BREAKPOINTS}
      cols={COLS}
      rowHeight={80}
      margin={[16, 16]}
      isDraggable={false}
      isResizable={false}
    >
      {rglLayout.map(({ i }) => {
        const item = itemsById[i];
        const widget = widgetRegistry[item.type];
        const Widget = widget?.Component || UnknownWidget;
        const config = { ...(widget?.defaultConfig || {}), ...(item.config || {}) };

        return (
          <div key={i}>
              <Widget config={config} title={item.title} id={item.i} type={item.type} />
          </div>
        );
      })}
    </ResponsiveGridLayout>
  );
};

export default DashboardGridReadOnly;